import { useEffect, useMemo, useState } from 'react'
import Image from 'next/image'
import cn from 'classnames'

import { useTranslation } from 'utils/use-translation'
import { IFormattedLoan } from 'store/fetchers/loans/enum'
import { useAppSelector } from 'store/hooks'
import { allWallets } from 'store/reducers/wallets'
import { selectedUserSummary } from 'store/reducers/users'
import { expandExponential, formatBigNumberWithDynamicPrecision, formatNumber } from 'utils/formatters'
import { floorBigNumber, floorNumber } from 'utils/floor-number'

import IconArrow from 'images/icon-arrow.svg'

import styles from './styles.module.scss'
import { getCurrencies } from 'store/reducers/currencies'
import Skeleton from 'components/ui/skeleton'
import Tooltip from 'components/global/tooltip'
import useViewport from 'libs/use-viewport'
import { countDecimalPlaces } from 'utils/count-decimal-places'
import Notice from 'components/ui/notice'

interface ILiquidationPortfolio {
    loans: IFormattedLoan[]
}

interface ILoanView {
    coin: string
    network: string
    rate: string
    collateralAmount: string
    collateralAmountUsd: string
    repaymentAmountUsd: string
    coinImg: string
}

export const RenderAmount: React.FC<{ 
    value: string, 
    precision?: number, 
    mobileVersion?: boolean,
    maxLength?: number 
}> = ({ value, precision, mobileVersion = false, maxLength }) => {
    if (mobileVersion) {
        return <>{formatNumber(floorBigNumber(value, precision))}</>
    }

    return <>
        {formatBigNumberWithDynamicPrecision(value, 'floor', {
            minPrecision: precision ?? 6,
            maxLength: maxLength ?? 12,
        })}

        {formatBigNumberWithDynamicPrecision(value, 'floor', {
            minPrecision: precision ?? 6,
            maxLength: maxLength ?? 12,
        }).startsWith('0.0...') ? (
            <Tooltip className={styles.tooltip}>
                {expandExponential(value)}
            </Tooltip>
        ) : floorBigNumber(value, precision ?? 6)?.length > (maxLength ?? 12) ? (
            <Tooltip className={styles.tooltip}>
                {precision ? formatNumber(floorBigNumber(value, precision)) : formatNumber(value)}
            </Tooltip>
        ) : null}
    </>
}

const LiquidationPortfolio = ({ loans }: ILiquidationPortfolio) => {
    const { t } = useTranslation()
    const [view] = useViewport()
    const isMobile = ['xs-mobile', 'mobile'].includes(view)
    const wallets = useAppSelector(allWallets)
    const loansSummary = useAppSelector(selectedUserSummary)?.loan
    const currencies = useAppSelector(getCurrencies)

    const [isToggled, setIsToggled] = useState(true)
    const [loansView, setLoansView] = useState<ILoanView[]>([])

    const onToggle = () => setIsToggled(prev => !prev)

    useEffect(() => {
        if (!loans.length || !loansSummary) return

        const sorted = [...loansSummary?.groups]
            .map(({ amount, depositAmount, repaymentAmountUsd, code, network }) => {
                const currency = currencies.find(
                    c =>
                        c.code.toLowerCase() === code?.toLowerCase() &&
                        c.network.toLowerCase() === network?.toLowerCase()
                )
                const rate = wallets.find(
                    w =>
                        w.code.toLowerCase() === code?.toLowerCase() &&
                        w.network.toLowerCase() === network?.toLowerCase()
                )?.rate ?? '0'

                return {
                    coin: code?.toUpperCase(),
                    network: network?.toUpperCase(),
                    rate: rate,
                    collateralAmount: depositAmount,
                    collateralAmountUsd: amount,
                    repaymentAmountUsd: repaymentAmountUsd,
                    coinImg: currency?.logo_url ?? '',
                }
            })

        setLoansView(sorted)
    }, [loans, loansSummary, currencies, wallets])

    const totalDepositAmount = loansSummary?.depositAmountReducedInUsd ?? '0'
    const totalCollateralLiquidationPrice = (+(loansSummary?.repaymentAmountReducedInUsdt ?? '0') * 1.05)?.toString()

    const percentDiff = useMemo(() => {
        if (!totalCollateralLiquidationPrice) return 0

        const difference = +totalDepositAmount - +totalCollateralLiquidationPrice
        const percent = (difference / +totalDepositAmount) * 100

        return countDecimalPlaces(percent) > 2 ? floorNumber(percent, 2) : percent
    }, [totalDepositAmount, totalCollateralLiquidationPrice])

    return (
        <section className={cn(styles.container)}>
            <div className={styles['container-header']}>
                <div className={styles['container-content']}>
                    <h2 className={styles.container__title}>{t('portfolio')}</h2>
                    <p className={styles.container__text}>{t('manage_collateral_to_avoid_liquidation')}</p>
                </div>
                <div className={styles.container__arrow} onClick={onToggle}>
                    <img
                        alt="arrow"
                        src={IconArrow}
                        className={cn({ [styles.close]: !isToggled })}
                    />
                </div>
            </div>

            <div
                className={cn(
                    styles.container__block,
                    { [styles.container__hidden]: !isToggled },
                    { [styles.container__visible]: isToggled }
                )}
            >
                {loansSummary && loansView?.length
                    ? <>
                        <div className={styles['loans-data']}>
                            <div className={styles['loans-data__item']}>
                                <p>{t('total_collateral')}</p>
                                <p className={styles.hasTooltip}>$<RenderAmount
                                    value={totalDepositAmount} 
                                    precision={2} 
                                    mobileVersion={isMobile}
                                    maxLength={16}
                                    /></p>
                            </div>

                            <div className={styles.line}></div>

                            <div className={styles['loans-data__item']}>
                                <p>{t('total_collateral_liquidation_price')}</p>
                                <p className={styles.hasTooltip}>$<RenderAmount 
                                    value={totalCollateralLiquidationPrice} 
                                    precision={2} 
                                    mobileVersion={isMobile}
                                    maxLength={16}
                                    /></p>
                            </div>

                            <div className={styles.line}></div>

                            <div className={styles['loans-data__item']}>
                                <p className={styles['notice-wrapper']}>
                                    {t('amount_difference')}
                                    <Notice alt={'difference-notice'} big type="secondary" className={styles['with-notice']}>
                                        {t('loans_will_be_liquidated_upon_reaching')}
                                    </Notice>
                                </p>
                                <p className={styles.hasTooltip}>
                                    <span>$<RenderAmount 
                                        value={Math.abs(+totalDepositAmount - +totalCollateralLiquidationPrice)?.toString()} 
                                        precision={2} 
                                        mobileVersion={isMobile}
                                        maxLength={8}
                                        /></span>
                                    <span className={cn(styles.percent,
                                        percentDiff > 5 ? styles.percent_green : styles.percent_red)}>{percentDiff}%</span>
                                </p>
                            </div>
                        </div>

                        <div className={styles['loans-table-wrapper']}>
                            {loansView?.length > 0 &&
                                <div className={styles['loans-table']}>
                                    <div className={styles['loans-table__row']}>
                                        <small>{t('coin')}</small>
                                        <small>{t('collateral')}</small>
                                        <small>{t('rate')}</small>
                                        <small>{t('collateral_amount_usd')}</small>
                                        <small>{t('repayment_amount_usd')}</small>
                                    </div>

                                    {loansView.map((view, index) => (
                                        <div key={index} className={styles['loans-table__row']}>
                                            <div className={styles.coin}>
                                                {view.coinImg && (
                                                    <Image src={view.coinImg} width={20} height={20} alt={view.coin} />
                                                )}
                                                <p className={styles.oneLine}>{view.network ? view.coin : t('other')}</p>
                                            </div>
                                            <p className={styles.hasTooltip}><RenderAmount value={view?.collateralAmount} /></p>
                                            <p className={styles.hasTooltip}>$<RenderAmount value={view?.rate} /></p>
                                            <p className={styles.hasTooltip}>$<RenderAmount value={view?.collateralAmountUsd} precision={2}/></p>
                                            <p className={styles.hasTooltip}>$<RenderAmount value={view?.repaymentAmountUsd} precision={2}/></p>
                                        </div>
                                    ))}
                                </div>
                            }
                        </div>
                    </> : <Skeleton className={styles.skeleton} />}

            </div>
        </section>
    )
}

export default LiquidationPortfolio